import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { CompanyForm } from "@/components/forms/CompanyForm";
import { PersonForm } from "@/components/forms/PersonForm";
import { ProjectWithRelations } from "@/hooks/useProjects";
import {
  useCompany,
  useUpdateCompany,
  type CompanyInsert,
  type CompanyUpdate,
} from "@/hooks/useCompanies";
import {
  usePerson,
  useUpdatePerson,
  type PersonInsert,
  type PersonUpdate,
} from "@/hooks/usePeople";
import { useAuth } from "@/hooks/useAuth";
import { formatDateRange, formatDate } from "@/lib/date-utils";
import { getLanguageLabel } from "@/lib/languages";
import {
  Building2,
  User,
  Calendar,
  FileText,
  Users,
  Globe,
  Settings,
  Pencil,
  MapPin,
} from "lucide-react";
import { useParams, Link } from "react-router-dom";
import { ProjectContactsSection } from "./ProjectContactsSection";

interface ProjectOverviewTabProps {
  project: ProjectWithRelations;
}

export function ProjectOverviewTab({ project }: ProjectOverviewTabProps) {
  const { isEmployee } = useAuth();
  const { id } = useParams<{ id: string }>();

  const [editCustomerDialogOpen, setEditCustomerDialogOpen] = useState(false);
  const [editContactDialogOpen, setEditContactDialogOpen] = useState(false);

  const { data: customerData } = useCompany(project.customer_id ?? undefined);
  const { data: contactData } = usePerson(project.contact_id ?? undefined);
  const updateCompany = useUpdateCompany();
  const updatePerson = useUpdatePerson();

  const formatPersonName = (
    person: { first_name: string; last_name: string } | null,
  ) => {
    if (!person) return null;
    return `${person.first_name} ${person.last_name}`;
  };

  const settingsHref = `/projects/${id}/settings`;

  const handleUpdateCustomer = async (data: CompanyInsert | CompanyUpdate) => {
    if (!project.customer_id) return;
    await updateCompany.mutateAsync({ id: project.customer_id, updates: data });
    setEditCustomerDialogOpen(false);
  };

  const handleUpdateContact = async (data: PersonInsert | PersonUpdate) => {
    if (!project.contact_id) return;
    await updatePerson.mutateAsync({ id: project.contact_id, updates: data });
    setEditContactDialogOpen(false);
  };

  return (
    <>
      <div className="grid gap-6 md:grid-cols-2">
        {/* Project Details */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-lg">Project Details</CardTitle>
            {isEmployee && (
              <Button
                variant="ghost"
                size="icon"
                asChild
                title="Edit in Settings"
              >
                <Link to={settingsHref}>
                  <Settings className="h-4 w-4" />
                </Link>
              </Button>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            {project.number && (
              <div className="flex items-start gap-3">
                <FileText className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    Project Number
                  </p>
                  <p className="text-foreground font-mono">{project.number}</p>
                </div>
              </div>
            )}

            <div className="flex items-start gap-3">
              <Building2 className="h-5 w-5 text-muted-foreground mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-muted-foreground">
                  Customer
                </p>
                <div className="flex items-center gap-1">
                  <p className="text-foreground">
                    {project.companies?.name || "Not assigned"}
                  </p>
                  {isEmployee && project.customer_id && (
                    <Button
                      offlineEdit
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => setEditCustomerDialogOpen(true)}
                    >
                      <Pencil className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <User className="h-5 w-5 text-muted-foreground mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-muted-foreground">
                  Contact Person
                </p>
                <div className="flex items-center gap-1">
                  <p className="text-foreground">
                    {formatPersonName(project.contact) || "Not assigned"}
                  </p>
                  {isEmployee && project.contact_id && (
                    <Button
                      offlineEdit
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => setEditContactDialogOpen(true)}
                    >
                      <Pencil className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Globe className="h-5 w-5 text-muted-foreground mt-0.5" />
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Language
                </p>
                <p className="text-foreground">
                  {getLanguageLabel(project.language)}
                </p>
              </div>
            </div>

            {project.production_mode && (
              <div className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    Production Mode
                  </p>
                  <p className="text-foreground">
                    {project.production_mode === "remote"
                      ? "Remote Production"
                      : "On Site Production"}
                  </p>
                </div>
              </div>
            )}

            {project.production_location && (
              <div className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    Production Location
                  </p>
                  <p className="text-foreground">
                    {project.production_location}
                  </p>
                </div>
              </div>
            )}

            <div className="flex items-start gap-3">
              <Calendar className="h-5 w-5 text-muted-foreground mt-0.5" />
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Event Date
                </p>
                <p className="text-foreground">
                  {formatDateRange(
                    project.event_start_date,
                    project.event_end_date,
                  )}
                </p>
              </div>
            </div>

            {(project.planning_start_date || project.planning_end_date) && (
              <div className="flex items-start gap-3">
                <Calendar className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    Planning Date
                  </p>
                  <p className="text-foreground">
                    {formatDateRange(
                      project.planning_start_date,
                      project.planning_end_date,
                    )}
                  </p>
                </div>
              </div>
            )}

            {project.project_groups?.name && project.project_group_id && (
              <div className="flex items-start gap-3">
                <Users className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    Project Group
                  </p>
                  <Link
                    to={`/projects/groups/${project.project_group_id}`}
                    className="text-foreground hover:underline"
                  >
                    {project.project_groups.name}
                  </Link>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Team */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-lg">Team</CardTitle>
            {isEmployee && (
              <Button
                variant="ghost"
                size="icon"
                asChild
                title="Edit in Settings"
              >
                <Link to={settingsHref}>
                  <Settings className="h-4 w-4" />
                </Link>
              </Button>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-sm font-medium text-muted-foreground">
                Account Manager
              </p>
              <p className="text-foreground">
                {formatPersonName(project.account_manager) || "Not assigned"}
              </p>
            </div>

            <div>
              <p className="text-sm font-medium text-muted-foreground">
                Project Manager
              </p>
              <p className="text-foreground">
                {formatPersonName(project.project_manager) || "Not assigned"}
              </p>
            </div>

            <div>
              <p className="text-sm font-medium text-muted-foreground">
                Technical Responsible
              </p>
              <p className="text-foreground">
                {formatPersonName(project.technical_responsible) ||
                  "Not assigned"}
              </p>
            </div>

            <div>
              <p className="text-sm font-medium text-muted-foreground">
                Equipment Responsible
              </p>
              <p className="text-foreground">
                {formatPersonName(project.equipment_responsible) ||
                  "Not assigned"}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Contacts */}
        <ProjectContactsSection projectId={project.id} readOnly />

        {/* Notes */}
        {project.notes && (
          <Card className="md:col-span-2">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Notes
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-foreground whitespace-pre-wrap">
                {project.notes}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Metadata */}
        <Card className="md:col-span-2">
          <CardHeader>
            <CardTitle className="text-lg">Metadata</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-6 text-sm">
              <div>
                <p className="text-muted-foreground">Created</p>
                <p className="text-foreground">
                  {formatDate(project.created_at)}
                </p>
              </div>
              <div>
                <p className="text-muted-foreground">Last Updated</p>
                <p className="text-foreground">
                  {formatDate(project.updated_at)}
                </p>
              </div>
              <div>
                <p className="text-muted-foreground">Version</p>
                <p className="text-foreground">{project.version}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Edit Customer Dialog */}
      <Dialog
        open={editCustomerDialogOpen}
        onOpenChange={setEditCustomerDialogOpen}
      >
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Customer</DialogTitle>
          </DialogHeader>
          {customerData && (
            <CompanyForm
              company={customerData}
              onSubmit={handleUpdateCustomer}
              onCancel={() => setEditCustomerDialogOpen(false)}
              isLoading={updateCompany.isPending}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Contact Dialog */}
      <Dialog
        open={editContactDialogOpen}
        onOpenChange={setEditContactDialogOpen}
      >
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Contact Person</DialogTitle>
          </DialogHeader>
          {contactData && (
            <PersonForm
              person={contactData}
              onSubmit={handleUpdateContact}
              onCancel={() => setEditContactDialogOpen(false)}
              isLoading={updatePerson.isPending}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
